'use client';

import { useEffect } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { COOKIE_KEY } from '@/app/common/cookies/types';
import { getCookie } from '@/app/common/cookies/clientCookies';
import {
  VIEWED_HOTELS_VISIBLE_COOKIE_BUDGET_BYTES,
  fitViewedHotelsToBudget,
  parseViewedHotels,
  pushViewedHotel,
  removeViewedHotelsCookie,
  serialiseViewedHotels,
  utf8Bytes,
  viewedHotelsEntryBytes,
  writeViewedHotelsCookie,
} from '@/app/common/viewedHotels/viewedHotelsCookie';
import { clientAppLog, createCodeSourceName } from '@/app/components/search/logging/clientAppLog';
import { isValidHotelId } from '../common/common.helper';
import { useInitialParams } from '../InitialParamsProvider';

const CODE_SOURCE_NAME = 'useRememberViewedHotel';

const readRawCookie = (): string | undefined => {
  try {
    return getCookie(COOKIE_KEY.VIEWED_HOTELS);
  } catch {
    return undefined;
  }
};

// An undecodable value makes `getCookie` throw, but the cookie is still there and must be deleted when it applies.
const hasCookie = (): boolean =>
  document.cookie.split('; ').some((entry) => entry.startsWith(`${COOKIE_KEY.VIEWED_HOTELS}=`));

/**
 * MPH-1291: remembers the hotel being viewed in the `tsx_vh` cookie (the last 10 `internalHotelId`s, see
 * `viewedHotelsCookie.ts` for the contract), once per hotel. Behind `viewedHotelsCookieEnabled`; when it is off the
 * cookie is deleted (only here, on the Evo hotel detail). When the cookies visible to the browser leave no room for ten
 * ids, the oldest are dropped; when not even the current hotel fits, nothing is written and the cookie is deleted. No UI.
 */
export function useRememberViewedHotel() {
  const { internalHotelId } = useInitialParams();
  const { localization } = usePageWrapperData();
  const viewedHotelsCookieEnabled = !!localization?.configValuesApp?.viewedHotelsCookieEnabled;

  useEffect(() => {
    const currentValue = readRawCookie();

    if (!viewedHotelsCookieEnabled) {
      if (hasCookie()) removeViewedHotelsCookie();
      return;
    }
    if (!isValidHotelId(internalHotelId)) return;

    const updatedHotels = pushViewedHotel(parseViewedHotels(currentValue), internalHotelId);
    const viewedHotels = fitViewedHotelsToBudget(document.cookie, currentValue, updatedHotels);
    if (!viewedHotels) {
      const deletedCookie = hasCookie();
      if (deletedCookie) removeViewedHotelsCookie();
      clientAppLog({
        level: 'INFO',
        codeSourceName: createCodeSourceName(CODE_SOURCE_NAME),
        logMessage:
          `[VIEWED_HOTELS_COOKIE_SKIPPED] visibleCookieBytes=${utf8Bytes(document.cookie)} ` +
          `entryBytes=${viewedHotelsEntryBytes(serialiseViewedHotels(updatedHotels))} ` +
          `budgetBytes=${VIEWED_HOTELS_VISIBLE_COOKIE_BUDGET_BYTES} deletedCookie=${deletedCookie}`,
        url: window.location.href,
      });
      return;
    }
    writeViewedHotelsCookie(viewedHotels);
  }, [internalHotelId, viewedHotelsCookieEnabled]);
}
